<!-- Responsibility: Render the active game-event overlay while preserving scene-level interaction.
Scope: Owns event overlay rendering/input only; event state lives in the shared game store. -->
<event-modal>
  <aside if={ state.isOpen } class="event-modal-shell" aria-live="polite">
    <section class="event-modal-card" role="dialog" aria-modal="false" aria-labelledby="event-modal-title">
      <div class="event-modal-media { state.imageUrl ? 'has-image' : 'fallback' }">
        <img if={ state.imageUrl } src={ state.imageUrl } alt={ state.imageAlt }>
        <div if={ !state.imageUrl } class="event-modal-glyph" aria-hidden="true">{ state.imageGlyph }</div>
        <span class="event-modal-kicker">GAME EVENT</span>
      </div>

      <div class="event-modal-body">
        <h2 id="event-modal-title">{ state.title }</h2>
        <raw-html if={ state.desc } class="event-modal-desc" mode="prose" content={ state.desc }></raw-html>

        <div class="event-modal-choices" if={ state.choices.length }>
          <button
            each={ choice in state.choices }
            type="button"
            class="event-choice"
            disabled={ state.busy }
            onclick={ () => submitAction(choice.input, choice.label, 'event-choice') }
          >{ choice.label }</button>
        </div>

        <form class="event-modal-input" onsubmit={ onSubmit }>
          <input
            ref="customInput"
            type="text"
            placeholder="Resolve this event in your own words..."
            value={ state.customInput }
            disabled={ state.busy }
            oninput={ onInputChange }
            autocomplete="off"
          >
          <button type="submit" disabled={ state.busy || !state.customInput.trim() }>SEND</button>
        </form>

        <p class="event-modal-meta">
          Scene controls remain available while this event is active.
        </p>
      </div>
    </section>
  </aside>

  <script>
    import { dispatcher } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import * as gameStore from '../../game-store.js'
    import RawHtml from '../components/raw-html.riot'

    export default {
      components: { RawHtml },

      state: {
        isOpen: false,
        busy: false,
        title: '',
        desc: '',
        choices: [],
        customInput: '',
        imageUrl: '',
        imageAlt: '',
        imageGlyph: '✦',
      },

      onMounted() {
        this.handles = [
          dispatcher.on(TOPICS.game.started, () => this.syncFromStore()),
          dispatcher.on(TOPICS.game.loaded, () => this.syncFromStore()),
          dispatcher.on(TOPICS.game.actionStart, () => this.syncFromStore()),
          dispatcher.on(TOPICS.game.actionUpdate, () => this.syncFromStore()),
          dispatcher.on(TOPICS.game.actionComplete, () => this.syncFromStore()),
          dispatcher.on(TOPICS.game.actionFailed, () => this.syncFromStore()),
        ]
        this.syncFromStore()
      },

      onUnmounted() {
        this.handles?.forEach(handle => dispatcher.off(handle))
      },

      syncFromStore() {
        const gs = gameStore.state
        const event = gs?.activeEvent ?? null
        const previousEventId = this.state.isOpen ? this.state.eventId : ''
        const nextEventId = String(event?.id ?? '')
        const nextState = {
        eventId: nextEventId,
        isOpen: !!event,
          busy: !!gameStore.isProcessing,
          title: String(event?.title ?? '').trim(),
          desc: String(event?.desc ?? '').trim(),
          choices: Array.isArray(event?.choices) ? event.choices : [],
          ...this.resolveEventArt(gs, event),
        }
        if (previousEventId !== nextEventId) nextState.customInput = ''
        this.update(nextState)
      },

      resolveEventArt(gs, event) {
        const kind = String(event?.imageKind ?? 'scene').trim()
        const seed = String(event?.imageSeed ?? '').trim().toLowerCase()
        const companion = (gs?.companions ?? []).find(c => {
          const hay = `${c.name} ${c.identity}`.toLowerCase()
          return seed && hay.includes(seed)
        })
        const sceneImage = String(gs?.visionUrl ?? '').trim()
        const companionImage = String(companion?.portraitUrl ?? '').trim()
        const glyphMap = {
          scene: '◈',
          npc: '✦',
          companion: '☉',
          item: '◆',
        }
        const fallbackAlt = event?.title ? `${event.title} artwork` : 'Event artwork'
        if (kind === 'companion' && companionImage) {
          return { imageUrl: companionImage, imageAlt: `${companion.name} portrait`, imageGlyph: glyphMap.companion }
        }
        if (sceneImage) {
          return { imageUrl: sceneImage, imageAlt: fallbackAlt, imageGlyph: glyphMap[kind] || glyphMap.scene }
        }
        return { imageUrl: '', imageAlt: fallbackAlt, imageGlyph: glyphMap[kind] || glyphMap.scene }
      },

      onInputChange(event) {
        this.update({ customInput: String(event?.target?.value ?? '') })
      },

      onSubmit(event) {
        event?.preventDefault?.()
        this.submitAction(this.state.customInput, '', 'free-text')
      },

      submitAction(input, choiceLabel = '', toolKind = 'free-text') {
        const action = String(input ?? '').trim()
        if (!action || this.state.busy) return
        this.update({ customInput: '' })
        const eventId = String(this.state.eventId ?? '').trim()
        const eventTitle = String(this.state.title ?? '').trim()
        gameStore.handleAction(action, undefined, {
          source: 'event',
          tool: {
            kind: toolKind,
            id: eventId || 'active_event',
            label: String(choiceLabel ?? '').trim() || 'Event Input',
          },
          explicitTargets: [
            {
              kind: 'event',
              id: eventId || 'active_event',
              label: eventTitle || 'Active Event',
            },
          ],
          eventId,
          eventTitle,
          tags: ['event_modal'],
        })
      },
    }
  </script>

  <style>
    event-modal {
      display: contents;
    }

    .event-modal-shell {
      position: fixed;
      inset: auto 0 5.5rem 0;
      z-index: var(--z-scene-floating, 120);
      display: flex;
      justify-content: center;
      pointer-events: none;
      padding: 0 1rem;
    }

    .event-modal-card {
      pointer-events: auto;
      inline-size: min(42rem, 100%);
      display: grid;
      grid-template-columns: minmax(8rem, 11rem) minmax(0, 1fr);
      border: 0.08rem solid color-mix(in srgb, var(--gold) 52%, var(--border));
      border-radius: 1.4rem;
      background:
        linear-gradient(135deg, color-mix(in srgb, var(--surface-raised) 92%, transparent), color-mix(in srgb, var(--bg) 90%, transparent)),
        var(--surface-card-2);
      box-shadow:
        0 1.2rem 2.8rem rgba(0, 0, 0, 0.34),
        inset 0 0.08rem 0 rgba(255, 255, 255, 0.06);
      overflow: clip;
      backdrop-filter: blur(1rem);
    }

    .event-modal-media {
      position: relative;
      min-block-size: 100%;
      background:
        radial-gradient(circle at top, color-mix(in srgb, var(--gold) 26%, transparent), transparent 62%),
        linear-gradient(160deg, var(--surface-card-1), color-mix(in srgb, var(--surface-card-3) 84%, var(--bg)));
    }

    .event-modal-media img {
      inline-size: 100%;
      block-size: 100%;
      object-fit: cover;
      display: block;
    }

    .event-modal-media.fallback {
      display: grid;
      place-items: center;
    }

    .event-modal-glyph {
      font-size: clamp(2.2rem, 4vw, 3.4rem);
      color: var(--gold);
      text-shadow: 0 0 1.2rem var(--gold-glow);
    }

    .event-modal-kicker {
      position: absolute;
      inset: 0.8rem auto auto 0.8rem;
      padding: 0.24rem 0.6rem;
      border-radius: 999rem;
      background: rgba(0, 0, 0, 0.42);
      border: 0.08rem solid rgba(255, 255, 255, 0.08);
      color: var(--gold);
      letter-spacing: 0.12em;
      font-size: 0.68rem;
      font-weight: 700;
    }

    .event-modal-body {
      display: grid;
      gap: 0.8rem;
      padding: 1rem 1rem 1rem 0.95rem;
    }

    .event-modal-body h2 {
      margin: 0;
      line-height: 1.05;
      font-size: clamp(1.1rem, 2vw, 1.5rem);
    }

    .event-modal-desc {
      color: var(--text-prose);
    }

    .event-modal-choices {
      display: flex;
      flex-wrap: wrap;
      gap: 0.55rem;
    }

    .event-choice,
    .event-modal-input button {
      border-radius: 999rem;
      border: 0.08rem solid color-mix(in srgb, var(--gold) 38%, var(--border));
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--surface-raised) 88%, transparent), color-mix(in srgb, var(--surface-card-3) 96%, transparent));
      color: var(--text-prose);
      font-weight: 700;
      letter-spacing: 0.03em;
    }

    .event-choice:hover,
    .event-modal-input button:hover {
      transform: translateY(-0.04rem);
      border-color: color-mix(in srgb, var(--gold) 62%, var(--border));
    }

    .event-modal-input {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 0.5rem;
    }

    .event-modal-input input {
      min-inline-size: 0;
      border-radius: 0.9rem;
      border: 0.08rem solid var(--border);
      background: color-mix(in srgb, var(--surface-card-1) 92%, black 8%);
      color: var(--text-prose);
      padding: 0.72rem 0.84rem;
    }

    .event-modal-meta {
      margin: 0;
      color: var(--text-dim);
      font-size: 0.8rem;
    }

    @media (max-width: 44rem) {
      .event-modal-shell {
        inset: auto 0 5rem 0;
        padding: 0 0.6rem;
      }

      .event-modal-card {
        grid-template-columns: 1fr;
      }

      .event-modal-media {
        min-block-size: 9rem;
      }

      .event-modal-body {
        padding: 0.9rem;
      }

      .event-modal-input {
        grid-template-columns: 1fr;
      }
    }
  </style>
</event-modal>
